import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Skeleton as ChakraSkeleton } from "@chakra-ui/react";
import { cn } from "@peerigon/pupper/tailwind";
import Link from "next/link";
import { Fragment } from "react";
import ReactPaginate from "react-paginate";
import { useTranslatedString } from "src/i18n/hooks";
import { NotFoundIcon } from "./NotFoundIcon";
import { useSearchContext } from "./hooks";
import { CommonSearchTerms } from "./shared";

export const SearchResult: React.FC = () => {
	const t = useTranslatedString();
	const {
		state,
		data,
		isEmptyInput,
		isLoading,
		isEmptyResult,
		isError,
		commonSearchTerms,
	} = useSearchContext();

	const common = {
		className: "overflow-hidden w-full max-w-container  mt-0 flex flex-col",
	};

	if (isEmptyInput) {
		if (commonSearchTerms.length === 0)
			return <div className={cn(common.className, "block lg:hidden")} />;

		return (
			<div {...common}>
				<CommonSearchTerms />
			</div>
		);
	}

	if (isLoading)
		return (
			<div {...common}>
				{Array.from({ length: 3 }).map((_, index) => (
					// eslint-disable-next-line react/no-array-index-key
					<div className="flex items-center gap-1 py-2" key={index}>
						<div className="flex flex-col gap-2 w-full">
							<ChakraSkeleton className="h-4 lg:h-5 w-full" />
							<ChakraSkeleton className="h-3 lg:h-4 w-3/4" />
						</div>
					</div>
				))}
			</div>
		);

	if (isError || isEmptyResult)
		return (
			<div
				className={cn(
					common.className,
					"flex flex-col items-center justify-center py-12",
				)}
			>
				<div className="p-6 mb-2 rounded-full border border-brand-light w-fit bg-white">
					<NotFoundIcon />
				</div>

				<span className="py-3 px-2 w-full text-center">
					{t("search.notFound")}
				</span>
			</div>
		);

	return (
		<div {...common}>
			<div className="flex flex-col gap-2 h-full overflow-y-auto pb-8">
				{data?.pageResults.map((result) => {
					const url = new URL(result.url);
					const pathname = url.pathname;

					return (
						<Fragment key={result.id}>
							<Link
								href={pathname}
								className="w-full py-2 flex gap-1 items-center"
							>
								<div>
									<p className="text-md underline font-bold hover:no-underline">
										{result.title}
									</p>
									<p className="text-sm">
										{t("search.text", {
											text: result.bodyExcerpt,
										})}
									</p>
								</div>
							</Link>
						</Fragment>
					);
				})}
			</div>
			{data && data.totalPages > 1 && (
				<ReactPaginate
					pageCount={data.totalPages}
					forcePage={state.page}
					onPageChange={({ selected }) => {
						state.setPage(selected);
					}}
					nextLabel={<ChevronRightIcon className="size-6!" />}
					previousLabel={<ChevronLeftIcon className="size-6!" />}
					className="bg-white w-fit shadow-md gap-0 p-3 mx-auto my-4 overflow-x-auto max-w-[calc(100%-2rem)] rounded-md flex list-none"
					nextClassName="ml-2 static mt-0 size-12 rounded-md border-0 box-shadow-none shrink-0 text-brand-blue bg-brand-light-bright hover:text-white hover:bg-brand-blue items-center justify-center inline-flex"
					previousClassName="mr-2 static mt-0 size-12 rounded-md border-0 box-shadow-none shrink-0 text-brand-blue bg-brand-light-bright hover:text-white hover:bg-brand-blue items-center justify-center inline-flex"
					nextLinkClassName="h-full w-full flex items-center justify-center"
					previousLinkClassName="h-full w-full flex items-center justify-center"
					disabledLinkClassName="cursor-default text-brand-light"
					disabledClassName="bg-brand-light-bright hover:bg-brand-light-bright cursor-default"
					activeLinkClassName="bg-brand-blue! text-white rounded-full"
					pageLinkClassName="flex flex-col justify-center items-center size-12 bg-transparent text-brand-blue font-medium shrink-0 cursor-pointer"
					breakClassName="flex items-center size-12 justify-center"
					marginPagesDisplayed={1}
					renderOnZeroPageCount={null}
				/>
			)}
		</div>
	);
};

// 🔬 TBD
